import { useEffect, useState, type FC } from "react";
import { motion, motionValue, useScroll } from "framer-motion";

import "./PageBackground.css";

type BackgroundImagesProps = {
  bgImage?: string;
  bgImageVert?: string;
};

export const PageBackground: FC<BackgroundImagesProps> = ({ bgImage, bgImageVert }) => {
  const { scrollY } = useScroll();
  const [halfViewportHeight, setHalfViewportHeight] = useState(500);
  const [currentBgImage, setCurrentBgImage] = useState<string | undefined>(bgImage);
  const [prevBgImage, setPrevBgImage] = useState<string | undefined>(undefined);
  const grainOpacity = motionValue(0.18);

  // Helper to sync scroll and background image
  const syncScroll = () => {
    const current = scrollY.get();
    // Set grain opacity depending on background color
    grainOpacity.set(
      window.getComputedStyle(document.body).getPropertyValue(`--background-color-rgb`) === "38, 48, 38" ? 0.3 : 0.18
    );
    const isPortrait = window.matchMedia("(orientation: portrait)").matches;
    const newImage = isPortrait ? bgImageVert : bgImage;
    if (current > halfViewportHeight) {
      if (currentBgImage !== `/img/grain.png`) {
        setPrevBgImage(currentBgImage);
        setCurrentBgImage(`/img/grain.png`);
      }
    } else {
      if (currentBgImage === `/img/grain.png` && currentBgImage !== newImage) {
        setPrevBgImage(currentBgImage);
        setCurrentBgImage(newImage);
      }
    }
  };

  useEffect(() => {
    const handleOrientationChange = () => {
      const isPortrait = window.matchMedia("(orientation: portrait)").matches;
      setCurrentBgImage(isPortrait ? bgImageVert : bgImage);
    };

    handleOrientationChange();
    syncScroll(); // Ensure correct initial background image based on scroll

    window.addEventListener("resize", handleOrientationChange);
    window.addEventListener("resize", syncScroll);

    return () => {
      window.removeEventListener("resize", handleOrientationChange);
      window.removeEventListener("resize", syncScroll);
    };
  }, [bgImage, bgImageVert, halfViewportHeight]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      syncScroll();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [bgImage, bgImageVert, halfViewportHeight]);

  // Debounced requestAnimationFrame scroll handler
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          syncScroll();
          ticking = false;
        });
        ticking = true;
      }
    };
    const unsubscribe = scrollY.on("change", handleScroll);
    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bgImage, bgImageVert, halfViewportHeight, currentBgImage, prevBgImage]);
  useEffect(() => {
    setHalfViewportHeight(window.innerHeight / 2);
  }, []);

  return (
    <div className="page-background-container">
      {prevBgImage && (
        <motion.div
          key={prevBgImage}
          className="page-background"
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          style={{
            backgroundImage: `url(${prevBgImage})`,
          }}
        />
      )}
      {currentBgImage && (
        <motion.div
          key={currentBgImage}
          className="page-background"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          style={{
            backgroundImage: `url(${currentBgImage})`,
          }}
        />
      )}
    </div>
  );
};
